import React, { useMemo, useState, useEffect } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useResources } from '@platform/core/src/context/ResourceContext';
import { getModalityIcon } from '../../config/modalityConfig';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import SyncIcon from '@mui/icons-material/Sync';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DynamicIcon from '../common/DynamicIcon';
import { getTrackableDefinitions } from '../../services/trackablesApi';

let _trackableDefsCache = null;

const BIO_LOG_COLORS = {
  WEIGHT: 'success',
  SLEEP: 'primary',
  NUTRITION: 'warning',
  MOOD: 'secondary',
  MEASUREMENT: 'info',
  DEFAULT: 'info'
};

const STANDARD_ICONS = {
  WEIGHT: 'weight',
  SLEEP: 'sleep',
  NUTRITION: 'nutrition',
  MOOD: 'mood',
  MEASUREMENT: 'measure'
};

const WorkoutEventContent = ({ eventInfo, isMobile }) => {
  const theme = useTheme();
  const { getResourceById, getResourcesByType } = useResources();
  const eventData = eventInfo.event.extendedProps;
  const allActivities = getResourcesByType('exercise_definition');
  const [trackableDefs, setTrackableDefs] = useState(_trackableDefsCache || []);

  useEffect(() => {
    if (!_trackableDefsCache) {
      getTrackableDefinitions().then(defs => {
        _trackableDefsCache = defs;
        setTrackableDefs(defs);
      }).catch(err => console.warn("Failed to load trackable icons", err));
    } else {
        setTrackableDefs(_trackableDefsCache);
    }
  }, []);

  const sessionId = useMemo(() => {
    if (!eventData.references || !Array.isArray(eventData.references)) return null;
    const ref = eventData.references.find(r =>
      r.parent_type === 'workout_session' || r.child_type === 'workout_session'
    );
    return ref ? (ref.parent_type === 'workout_session' ? ref.parent_id : ref.child_id) : null;
  }, [eventData]);

  const planId = useMemo(() => {
    if (!eventData.references || !Array.isArray(eventData.references)) return null;
    const ref = eventData.references.find(r =>
      r.parent_type === 'workout_plan' || r.child_type === 'workout_plan'
    );
    return ref ? (ref.parent_type === 'workout_plan' ? ref.parent_id : ref.child_id) : null;
  }, [eventData]);

  const bioLogId = useMemo(() => {
    if (!eventData.references || !Array.isArray(eventData.references)) return null;
    const ref = eventData.references.find(r =>
      r.parent_type === 'bio_log' || r.child_type === 'bio_log'
    );
    return ref ? (ref.parent_type === 'bio_log' ? ref.parent_id : ref.child_id) : null;
  }, [eventData]);

  const sessionResource = useMemo(() => sessionId ? getResourceById(sessionId) : null, [sessionId, getResourceById]);
  const planResource = useMemo(() => planId ? getResourceById(planId) : null, [planId, getResourceById]);
  const bioLogResource = useMemo(() => bioLogId ? getResourceById(bioLogId) : null, [bioLogId, getResourceById]);

  if (bioLogId) {
    const logType = bioLogResource?.log_type || 'WEIGHT';
    const logTypeUpper = logType.toUpperCase();
    
    // 1. Check standard system icons FIRST (Consistency with BioLogView)
    // 2. Check dynamic definition (Case-insensitive match)
    // 3. Fallback to 'note'
    
    let iconName = STANDARD_ICONS[logTypeUpper];
    
    if (!iconName) {
        const definition = trackableDefs.find(d => d.key.toUpperCase() === logTypeUpper);
        iconName = definition?.icon || 'note';
    }

    const definition = trackableDefs.find(d => d.key.toUpperCase() === logTypeUpper);
    const displayName = definition?.display_name || logType;
    const colorKey = BIO_LOG_COLORS[logTypeUpper] || 'primary';
    const color = theme.palette[colorKey].main;
    const bgColor = alpha(color, 0.15);
    const borderColor = alpha(color, 0.5);

    return (
      <div className="fc-event-main-frame" style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: bgColor,
            border: `1px solid ${borderColor}`,
            borderRadius: '4px',
            flexShrink: 0,
            cursor: 'pointer'
          }}
          title={`${displayName}: ${eventInfo.event.title}`}
        >
           <DynamicIcon icon={iconName} sx={{ color: color, fontSize: '1rem' }} />
        </Box>
      </div>
    );
  }

  const targetResource = sessionResource || planResource;
  const activitiesToAnalyze = useMemo(() => {
    if (!targetResource) return [];
    if (targetResource.activities) return targetResource.activities;
    if (targetResource.stages) return targetResource.stages.flatMap(s => s.activities || []);
    return [];
  }, [targetResource]);

  const topModality = useMemo(() => {
    if (!activitiesToAnalyze.length || !allActivities.length) return null;
    const modalityCounts = {};
    activitiesToAnalyze.forEach(act => {
      const def = allActivities.find(a => String(a.id) === String(act.exercise_definition_id));
      if (def) {
        const modalities = def.taxonomy?.modalities || [];
        if (modalities.length > 0) {
          // Use the primary modality for the icon
          modalityCounts[modalities[0]] = (modalityCounts[modalities[0]] || 0) + 1;
        }
      }
    });
    const sorted = Object.entries(modalityCounts).sort((a, b) => b[1] - a[1]);
    return sorted.length > 0 ? sorted[0][0] : null;
  }, [activitiesToAnalyze, allActivities]);

  if (isMobile) {
    if (sessionId || planId) {
      if (!targetResource) {
        return (
          <Stack direction="row" alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
            <SyncIcon sx={{ fontSize: '1rem', opacity: 0.5 }} />
          </Stack>
        );
      }
      const ModalityIcon = topModality ? getModalityIcon(topModality) : HelpOutlineIcon;
      const TypeIcon = sessionId ? HistoryIcon : AssignmentIcon;
      const typeColor = sessionId ? 'warning.main' : 'info.main';
      const displayName = targetResource.name || eventInfo.event.title;

      return (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            px: 0.5,
            py: 0.5,
            ...(sessionId && {
              border: `2px solid ${theme.palette.warning.main}`,
              borderRadius: '4px',
              boxSizing: 'border-box',
            })
          }}
        >
          <Typography
            variant="caption"
            sx={{
              width: '100%',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.7rem',
              lineHeight: 1.2,
              mb: 0.25,
              fontWeight: 500
            }}
          >
            {displayName}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
          >
            <TypeIcon sx={{ fontSize: '1rem', color: typeColor }} />
            <ModalityIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
          </Stack>
        </Stack>
      );
    }
    return (
      <div className="fc-event-main-frame" style={{ overflow: 'hidden', width: '100%', height: '100%' }}>
        <div className="fc-event-title-container" style={{ overflow: 'hidden' }}>
          <div className="fc-event-title" style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            <span>{eventInfo.event.title}</span>
          </div>
        </div>
      </div>
    );
  }

  const ModalityIcon = topModality ? getModalityIcon(topModality) : null;

  return (
    <div
      className="fc-event-main-frame"
      style={{
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        ...(sessionId && {
          border: `2px solid ${theme.palette.warning.main}`,
          borderRadius: '4px',
          padding: '1px',
        })
      }}
    >
      <div className="fc-event-title-container" style={{ overflow: 'hidden' }}>
        <div className="fc-event-title" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {sessionId && <HistoryIcon sx={{ fontSize: '1.1rem', color: 'warning.main', flexShrink: 0 }} />}
          {planId && !sessionId && <AssignmentIcon sx={{ fontSize: '1.1rem', color: 'info.main', flexShrink: 0 }} />}
          {ModalityIcon && (sessionId || planId) && <ModalityIcon sx={{ fontSize: '1.1rem', color: 'primary.main', flexShrink: 0 }} />}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{eventInfo.event.title}</span>
        </div>
      </div>
    </div>
  );
};

export default WorkoutEventContent;